import React from 'react';
import styled from '@emotion/styled';
import { Route, DecodedRouteParams } from 'config/routes';
import { typographyFontSizeCfg, typographyLineHeightCfg } from 'ds/components/typography/types';
import { Link } from 'components/link';
import { SmallText } from 'ds/components/typography';
import IconChevronRight from 'assets/svg/chevron-right.svg';

interface BreadcrumbItem {
  label: string;
  routeName?: Route;
  routeParams?: Partial<DecodedRouteParams>;
  isCurrent?: boolean;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  isFooter?: boolean;
  onItemClick?: (label: string, position: number) => void;
}

const BreadcrumbsRoot = styled.ol<{ isFooter: boolean; }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  ${({ isFooter, theme }) => isFooter ? '' : `gap: ${theme.spacing(0.5)};`}
`;

export const Breadcrumb = styled.li`
  display: flex;
  align-items: center;
  > a {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.neutrals.grey2};
    &:hover {
      color: ${({ theme }) => theme.colors.neutrals.grey1};
    }
  }

  .chevron-right-icon {
    ${({ theme }) => theme.isRTL ? 'transform: rotate(-180deg)' : ''};
    margin: 0 5px;
    path {
      fill: ${({ theme }) => theme.colors.neutrals.grey3};
    }
  }
`;

export const BreadcrumbText = styled(SmallText)`
  color: ${({ theme }) => theme.colors.neutrals.grey2};

  &:hover {
    color: ${({ theme }) => theme.colors.brand.main};
  }
`;

const LastBreadcrumbText = styled(SmallText)`
  color: ${({ theme }) => theme.colors.neutrals.grey1};
`;

const Separator = styled.div`
  :before {
    content: '/';
    font-size: ${typographyFontSizeCfg.smallText}px;
    line-height: ${typographyLineHeightCfg.smallText}px;
    color: ${({ theme }) => theme.colors.neutrals.grey2};
    padding-left: ${({ theme }) => theme.spacing(0.5)};
  }
`;


const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, isFooter, onItemClick }) => (
  <nav aria-label="breadcrumb">
    <BreadcrumbsRoot data-auto="breadcrumbs" isFooter={isFooter}>
      {items.map((item, index, arr) => (
        <Breadcrumb key={index} aria-current={item.isCurrent ? 'page' : undefined}>
          {index === 0 ? null : (isFooter ? <IconChevronRight /> : <Separator />)}
          {item.isCurrent ? (
            <LastBreadcrumbText>{item.label}</LastBreadcrumbText>
          ) : (
            item.routeName ? (
              <Link
                routeName={item.routeName}
                routeParams={item.routeParams}
                onClick={onItemClick ? () => onItemClick(item.label, index + 1) : undefined}
              >
                <BreadcrumbText>{item.label}</BreadcrumbText>
              </Link>
            ) : <BreadcrumbText>{item.label}</BreadcrumbText>
          )}
        </Breadcrumb>
      ))}
    </BreadcrumbsRoot>
  </nav>
);

export default Breadcrumbs;
